import { RequestStatus } from 'features/request/requestSlice';
import { ContractStatusEnum, ProjectStatusEnum, WorkOrderStatusEnum } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { Button, cnMixSpace } from 'uikit';

import type { PayloadAction } from '@reduxjs/toolkit';
import type { ForwardedRef } from 'react';
import { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { BimOrderRequisitesUpdate } from './BimOrderRequisitesUpdate';
import { useRequisitesDefaultValue } from './useRequisitesDefaultValue';

interface IBimOrderRequisites {
    isFrameContract: boolean;
    isLoading: boolean;
    orderId: number;
    withBtn?: boolean;
    onSuccess?: () => void;
    workOrderStatus?: WorkOrderStatusEnum;
    contractStatus?: ContractStatusEnum;
    orderRules?: IUseAccess['orderBim'];
    statusProject?: ProjectStatusEnum;
    bimWorkOrderVersionsRequest: (value: { workOrderId: number }) => PayloadAction;
    workOrderId: number;
    projectDataStatus: RequestStatus;
}

export const BimOrderRequisites = forwardRef(
    (props: IBimOrderRequisites, ref: ForwardedRef<{ openModalFromOutside: () => void }>) => {
        const {
            isFrameContract,
            orderId,
            onSuccess,
            statusProject,
            withBtn = true,
            isLoading,
            workOrderStatus,
            contractStatus,
            orderRules,
            bimWorkOrderVersionsRequest,
            workOrderId,
            projectDataStatus,
        } = props;

        const navigate = useNavigate();
        const { id: projectId } = useParams();

        const [isModalOpened, setIsModalOpened] = useState(false);

        const isContractStatusDelayed = contractStatus === ContractStatusEnum.Delayed;
        const isContractStatusCompleted = contractStatus === ContractStatusEnum.Completed;

        const isWorkOrderStatusCompleted = workOrderStatus === WorkOrderStatusEnum.Completed;
        const isWorkOrderStatusDelayed = workOrderStatus === WorkOrderStatusEnum.Delayed;
        const isProjectStatusDelayed = statusProject === ProjectStatusEnum.Delayed;

        const handleOnButtonClick = useCallback(() => {
            setIsModalOpened(true);
        }, []);

        const handleOnButtonClickFromOutSide = useCallback(() => {
            setTimeout(() => {
                setIsModalOpened(true);
            }, 100);
        }, []);

        const handleOnEditCancel = useCallback(() => {
            setIsModalOpened(false);
        }, []);

        useImperativeHandle(ref, () => ({
            openModalFromOutside: handleOnButtonClickFromOutSide,
        }));

        const successCallback = useCallback(() => {
            onSuccess?.();
            handleOnEditCancel();
        }, [handleOnEditCancel, onSuccess]);

        const { isOrderInfoFetching, defaultData } = useRequisitesDefaultValue({
            orderId,
            isFrameContract,
            isModalOpened,
            projectDataStatus,
        });

        const onEditStructureClick = useCallback(() => {
            navigate(`/projects/bim/${projectId}/order/${orderId}/structure`);
        }, [navigate, orderId, projectId]);

        const isDisabledButtonChangeStructure =
            isContractStatusDelayed ||
            isContractStatusCompleted ||
            isWorkOrderStatusCompleted ||
            isWorkOrderStatusDelayed ||
            !orderRules?.canEditStructure;

        return (
            <>
                {withBtn && (
                    <>
                        <Button
                            label="Редактировать данные"
                            size="s"
                            view="secondary"
                            className={cnMixSpace({ mR: 'xs' })}
                            onClick={handleOnButtonClick}
                            loading={isOrderInfoFetching || isLoading}
                            disabled={!orderRules?.canEdit || isProjectStatusDelayed}
                        />
                        <Button
                            label="Редактировать структуру"
                            size="s"
                            view="secondary"
                            style={{ marginRight: '16px' }}
                            onClick={onEditStructureClick}
                            disabled={isDisabledButtonChangeStructure || isProjectStatusDelayed}
                        />
                    </>
                )}
                {projectDataStatus === RequestStatus.SUCCESS && isModalOpened && (
                    <BimOrderRequisitesUpdate
                        onCancel={handleOnEditCancel}
                        onSuccess={successCallback}
                        isFrameContract={isFrameContract}
                        defaultValues={defaultData}
                        isLoading={isOrderInfoFetching}
                        contractStatus={contractStatus}
                        orderRules={orderRules}
                        bimWorkOrderVersionsRequest={bimWorkOrderVersionsRequest}
                        workOrderId={workOrderId}
                    />
                )}
            </>
        );
    },
);

BimOrderRequisites.displayName = 'BimOrderRequisites';
